/**
 * Plaster volume of a detected surface for the height profile the user is
 * looking at. Pure: no DOM, no three.js, runs in node tests and inside
 * `OccupancyHashWorker.js`.
 *
 * Input is the backend `heightRaster`: the same 4 cm UV cells area is counted
 * on, each carrying the count-weighted mean height of its member voxels
 * along the fitted normal. The volume fills every *highlighted* cell (the
 * ones the height profile colours: |h − level| ≤ range, measured along the
 * profile normal) up to a plane parallel to the profile reference through
 * the highest highlighted cell. Cells outside the range are not plaster.
 */
import {
  effectiveProfileRangeM,
  profileLevelFromCentroid,
  profileLevelM,
  profileNormal,
} from './surfaceProfile.js';

/** Sentinel for cells outside the area mask in `heightRaster.data`. */
export const HEIGHT_RASTER_EMPTY = 0x7fff;
/** `heightRaster.data` unit: 0.1 mm per step. */
export const HEIGHT_RASTER_SCALE = 10_000;

function finiteVec3(v) {
  return Array.isArray(v) && v.length >= 3 && Number.isFinite(v[0]) && Number.isFinite(v[1]) && Number.isFinite(v[2]);
}

function base64ToBytes(b64) {
  if (typeof atob === 'function') {
    const binary = atob(b64);
    const raw = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) raw[i] = binary.charCodeAt(i);
    return raw;
  }
  // node tests
  const buf = Buffer.from(b64, 'base64');
  return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
}

/**
 * Backend `heightRaster` → `{ e1, e2, minU, minV, width, height, cell, heights: Int16Array }`,
 * or null when the record is missing or inconsistent (older backends, bad data).
 */
export function decodeHeightRaster(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const { minU, minV, width, height, cellSizeM, data } = raw;
  if (!finiteVec3(raw.e1) || !finiteVec3(raw.e2)) return null;
  if (!Number.isFinite(minU) || !Number.isFinite(minV)) return null;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) return null;
  if (!Number.isFinite(cellSizeM) || cellSizeM <= 0) return null;
  if (typeof data !== 'string') return null;
  let bytes;
  try {
    bytes = base64ToBytes(data);
  } catch {
    return null;
  }
  if (bytes.byteLength !== width * height * 2) return null;
  // Copy into an aligned buffer: node's Buffer slices are not 2-byte aligned.
  const heights = new Int16Array(width * height);
  heights.set(new Int16Array(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)));
  return {
    e1: [raw.e1[0], raw.e1[1], raw.e1[2]],
    e2: [raw.e2[0], raw.e2[1], raw.e2[2]],
    minU,
    minV,
    width,
    height,
    cell: cellSizeM,
    heights,
  };
}

/** Raster cell centre at its mean height, offset frame. */
export function rasterCellPoint(raster, plane, i, hM) {
  const cx = i % raster.width;
  const cy = Math.floor(i / raster.width);
  // Cell x covers u ∈ [minU + (x−1)·cell, minU + x·cell) — see buildUvGrid.
  const u = raster.minU + (cx - 0.5) * raster.cell;
  const v = raster.minV + (cy - 0.5) * raster.cell;
  const o = plane.origin;
  const n = plane.normal;
  return [
    o[0] + raster.e1[0] * u + raster.e2[0] * v + n[0] * hM,
    o[1] + raster.e1[1] * u + raster.e2[1] * v + n[1] * hM,
    o[2] + raster.e1[2] * u + raster.e2[2] * v + n[2] * hM,
  ];
}

/**
 * Integrate the plaster volume over the highlighted cells.
 *
 * `plane` is the grown common plane (origin = pick, pre-flip normal),
 * `centroid` the grow centroid (height-profile zero level). Params mirror the
 * shader: `profileRef` ('fitted' | 'ideal'), `flipped`, `profileOffsetM`,
 * and the effective `rangeBelowM` / `rangeAboveM` (already resolved from the
 * pinned ranges / grown distance). A cell is highlighted iff
 * −rangeBelowM ≤ signedR ≤ rangeAboveM where
 * `signedR = (flipped ? −r : r) − level`, `r = dot(p − origin, profileNormal)`.
 * `volumeTop` picks the plaster top: `'highest'` (default) fills every
 * highlighted cell up to the highest highlighted one; `'plane'` fills only
 * up to the zero level — the proud cells are assumed chipped off and add 0.
 *
 * Returns `{ volumeM3, areaM2, cells, peakHeightM, topHeightM, extremes }`
 * with `extremes = { high, highHeightM, low, lowHeightM, topHeightM,
 * thicknessM, flipped } | null` — the highest and lowest highlighted cells
 * (offset frame), the plaster top height (`peakHeightM` or 0) and the
 * maximum plaster thickness `top − low`. `null` extremes and zeros when
 * nothing is highlighted.
 */
export function profileVolume(raster, plane, centroid, params = {}) {
  const empty = { volumeM3: 0, areaM2: 0, cells: 0, peakHeightM: 0, topHeightM: 0, extremes: null };
  if (!raster || !plane || !finiteVec3(plane.origin) || !finiteVec3(plane.normal)) return empty;
  const n = plane.normal;
  const pn = profileNormal(params.profileRef, n);
  const flipped = !!params.flipped;
  const sign = flipped ? -1 : 1;
  const level0 = profileLevelFromCentroid(centroid, { origin: plane.origin, normal: pn });
  const level = profileLevelM(flipped, level0, params.profileOffsetM);
  // Raw ranges, like the shader's cull: a zero side highlights nothing on
  // that side (the shader's 1e-4 floor is only in its gradient divisor).
  const below = effectiveProfileRangeM(params.rangeBelowM, undefined, 0.05);
  const above = effectiveProfileRangeM(params.rangeAboveM, undefined, 0.05);
  // dot(p − origin, pn) with p = origin + e1·u + e2·v + n·h.
  const e1p = raster.e1[0] * pn[0] + raster.e1[1] * pn[1] + raster.e1[2] * pn[2];
  const e2p = raster.e2[0] * pn[0] + raster.e2[1] * pn[1] + raster.e2[2] * pn[2];
  const np = n[0] * pn[0] + n[1] * pn[1] + n[2] * pn[2];
  const { width, cell, heights, minU, minV } = raster;
  const cellArea = cell * cell;

  // One pass: the highlighted set, its extremes and the two sums the volume
  // needs — no per-cell scratch (a yard raster is 2 M cells).
  let peak = -Infinity;
  let low = Infinity;
  let peakCell = -1;
  let lowCell = -1;
  let count = 0;
  let sumSigned = 0; // Σ signedR over the highlighted cells
  let sumHollow = 0; // Σ max(0, −signedR): the fill up to the zero level
  for (let i = 0; i < heights.length; i++) {
    const raw = heights[i];
    if (raw === HEIGHT_RASTER_EMPTY) continue;
    const h = raw / HEIGHT_RASTER_SCALE;
    const cx = i % width;
    const cy = (i - cx) / width;
    const u = minU + (cx - 0.5) * cell;
    const v = minV + (cy - 0.5) * cell;
    const r = u * e1p + v * e2p + h * np;
    const signedR = sign * r - level;
    if (signedR < -below || signedR > above) continue;
    count++;
    sumSigned += signedR;
    if (signedR < 0) sumHollow -= signedR;
    if (signedR > peak) {
      peak = signedR;
      peakCell = i;
    }
    if (signedR < low) {
      low = signedR;
      lowCell = i;
    }
  }
  if (count === 0) return empty;

  // Fill each highlighted cell up to the plaster top: the peak plane
  // (top ≥ every cell, so Σ (peak − s) = peak·count − Σ s), or the zero
  // level with everything proud of it chipped away (Σ max(0, −s)).
  const chip = params.volumeTop === 'plane';
  const top = chip ? 0 : peak;
  const volume = (chip ? sumHollow : peak * count - sumSigned) * cellArea;
  const pointOf = (i) => rasterCellPoint(raster, plane, i, heights[i] / HEIGHT_RASTER_SCALE);
  return {
    volumeM3: volume,
    areaM2: count * cellArea,
    cells: count,
    peakHeightM: peak,
    topHeightM: top,
    extremes: {
      high: pointOf(peakCell),
      highHeightM: peak,
      low: pointOf(lowCell),
      lowHeightM: low,
      topHeightM: top,
      thicknessM: Math.max(0, top - low),
      flipped,
    },
  };
}
